import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Collapse, Space, Spin, Switch, Table, Typography, message } from 'antd'
import fetchAPI from '../../../../../lib/utils/fetch-api'
import VekaSettings from '../../../../components/veka-integration/settings'

interface Point {
  external_id: string
  name: string
  partner: string
  office_id: number | null
  office_name: string | null
  enabled: boolean
  has_requests: boolean
  synced_until: string | null
  last_error: string | null
}

const TabVeka = ({ id }: { id: string }) => {
  const officeId = Number(id)
  const [points, setPoints] = useState<Point[]>([])
  const [selected, setSelected] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState('')
  const [keyConfigured, setKeyConfigured] = useState(false)
  const [importEnabled, setImportEnabled] = useState(false)
  const [testMode, setTestMode] = useState(false)
  const load = useCallback(async () => {
    setLoading(true)
    try {
      const result = await fetchAPI(`/api/veka/offices/${id}`)
      if (result.error || result.errors)
        throw new Error(result.errors?.join(', ') || result.error?.message)
      setPoints(result.mappings)
      setSelected(
        result.mappings
          .filter((point: Point) => point.office_id === Number(id))
          .map((point: Point) => point.external_id),
      )
      setKeyConfigured(result.key_configured)
      setImportEnabled(result.import_enabled)
      setTestMode(Boolean(result.test_mode))
      setDirty(false)
      setError('')
    } catch (e: any) {
      setError(e.message || 'Не удалось загрузить точки VEKA')
    } finally {
      setLoading(false)
    }
  }, [id])
  useEffect(() => {
    void load()
  }, [load])
  const save = async () => {
    setSaving(true)
    try {
      const result = await fetchAPI(`/api/veka/offices/${id}`, {
        method: 'POST',
        body: JSON.stringify({
          mappings: points
            .filter(point => selected.includes(point.external_id))
            .map(point => ({ external_id: point.external_id, enabled: point.enabled })),
        }),
      })
      if (result.error || result.errors)
        throw new Error(result.errors?.join(', ') || result.error?.message)
      message.success('Точки VEKA для офиса сохранены')
      await load()
    } catch (e: any) {
      message.error(e.message || 'Не удалось сохранить точки VEKA')
    } finally {
      setSaving(false)
    }
  }
  if (loading) return <Spin />
  if (error)
    return <Alert type="error" message={error} action={<Button onClick={load}>Повторить</Button>} />
  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      <Typography.Paragraph>
        Выберите точки продаж VEKA, заявки которых должны поступать в этот офис. Можно привязать
        несколько точек. Точки другого офиса недоступны для выбора. Для точки с заявками можно
        выключить обмен, сохранив привязку.
      </Typography.Paragraph>
      {testMode && (
        <Alert
          type="warning"
          message="Включён тестовый обмен VEKA. Заявки и результаты передаются через тестовую площадку."
        />
      )}
      {!keyConfigured && <Alert type="warning" message="API-ключ VEKA не настроен на сервере" />}
      {!importEnabled && (
        <Alert type="info" message="Получение заявок VEKA выключено в общих настройках" />
      )}
      <Table<Point>
        rowKey="external_id"
        dataSource={[...points].sort(
          (left, right) =>
            Number(right.office_id === officeId) - Number(left.office_id === officeId),
        )}
        pagination={false}
        scroll={{ x: 850 }}
        rowSelection={{
          selectedRowKeys: selected,
          onChange: keys => {
            setSelected(keys.map(String))
            setDirty(true)
          },
          getCheckboxProps: point => ({
            disabled:
              saving ||
              (point.office_id !== null && point.office_id !== officeId) ||
              (point.office_id === officeId && point.has_requests),
          }),
        }}
        columns={[
          { title: 'Партнёр', dataIndex: 'partner' },
          {
            title: 'Точка VEKA',
            render: (_, point) => (
              <>
                {point.name}
                <br />
                <Typography.Text type="secondary">{point.external_id}</Typography.Text>
              </>
            ),
          },
          {
            title: 'Офис ERP',
            render: (_, point) =>
              point.office_id === officeId ? 'Этот офис' : point.office_name || 'Не привязана',
          },
          {
            title: 'Обмен включён',
            render: (_, point) => (
              <Switch
                checked={selected.includes(point.external_id) && point.enabled}
                disabled={saving || !selected.includes(point.external_id) || !keyConfigured}
                onChange={enabled => {
                  setPoints(prev =>
                    prev.map(item =>
                      item.external_id === point.external_id ? { ...item, enabled } : item,
                    ),
                  )
                  setDirty(true)
                }}
              />
            ),
          },
          {
            title: 'Последняя загрузка',
            render: (_, point) => (
              <>
                {point.synced_until ? new Date(point.synced_until).toLocaleString('ru-RU') : '—'}
                {point.last_error && (
                  <Typography.Paragraph type="danger">{point.last_error}</Typography.Paragraph>
                )}
              </>
            ),
          },
        ]}
      />
      <Button type="primary" disabled={!dirty} loading={saving} onClick={save}>
        Сохранить сопоставление VEKA
      </Button>
      <Collapse
        items={[
          {
            key: 'common',
            label: 'Общие настройки обмена VEKA и журнал',
            children: dirty ? (
              <Alert type="info" message="Сначала сохраните сопоставление точек этого офиса" />
            ) : (
              <VekaSettings onSaved={load} />
            ),
          },
        ]}
      />
    </Space>
  )
}

export default TabVeka
